import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import GlobalHeader from "components/v2/global-header/GlobalHeader";
import { useNativeAppPromoVisibility } from "hooks/useNativeAppPromoVisibility";
import SharedNavigation, { NavigationIds } from "components/v2/shared-navigation/SharedNavigation";
import { Spinner } from "components/v2/spinner/spinner";
import useDesktopChecker from "hooks/useDesktopChecker";
import Tasks from "pages/dashboard/home/v2/tasks/Tasks";
import Documents from "pages/dashboard/v2/documents/Documents";
import Home from "pages/dashboard/v2/home/Home";
import LoanDetails from "pages/dashboard/home/v2/loan-details/LoanDetails";
import ProfileSettings from "pages/dashboard/v2/shared/profile-settings/ProfileSettings";
import { useDispatch, useSelector } from "react-redux";
import {
  getActiveTab,
  getCurrentUserId,
  getDealApplicationId,
  getIsInterimDashboard,
  getNhDealApplicationId,
  getProfileSettings,
  getTaskCount,
  setProfileActiveTab,
  setTaskCount,
  TaskFilterType,
  getDealId,
  getEmailAddress,
  getEncompassLoanId,
  getLoanId,
  getLoanNumber,
  getProspectId,
} from "features/authSlice";
import "./shared-layout.scss";
import GlobalOuterLayout from "../global-outer-layout/GlobalOuterLayout";
import { PalNotificationHandler } from "components/v2/notifications";
import { NotificationProvider, useNotificationContext } from "contexts/NotificationContext";
import useTabletChecker from "hooks/useTabletChecker";
import Footer from "components/footer/Footer";
import { NeedItem, useCreateNeedsMutation, useGetDashboardItemsQuery, useGetNeedsQuery } from "pages/dashboard/home/dashboardService";
import LoanDetailsMenu from "pages/dashboard/home/v2/loan-details-menu/LoanDetailsMenu";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { getEffectiveTaskStatusForCount } from "utilities/needsHelper";
import { getSessionHeldUnderReviewIds } from "pages/dashboard/store/dashboardSlice";
import { resetScrollPosition } from "utilities/resetScrollPosition";
import {
  DASHBOARD_CREATE_NEEDS_TRIGGERED,
  DASHBOARD_CREATE_NEEDS_TRIGGERED_TRUE,
  DASHBOARD_SESSION_ID,
  DASHBOARD_SESSION_START_TIMESTAMP,
} from "utilities/Constant";
import { trackFSDashboardPageNavigated } from "utilities/full-story";
import { getLoanOfficerPrimaryDomain } from "services/common/loanOfficerSlice";

interface SharedLayoutProps {
  propsActiveTab?: string;
}

const SharedLayoutContent: React.FC<SharedLayoutProps> = ({ propsActiveTab }: SharedLayoutProps) => {
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();
  const { isBannerVisible } = useNativeAppPromoVisibility();
  const dispatch = useDispatch();

  const reduxActiveTab = useSelector(getActiveTab);
  const showProfile = useSelector(getProfileSettings);
  const email = useSelector(getEmailAddress);
  const prospectId = useSelector(getProspectId);
  const loanNumber = useSelector(getLoanNumber);
  const encompassLoanId = useSelector(getEncompassLoanId);
  const loanId = useSelector(getLoanId);
  const dealId = useSelector(getDealId);
  const loPrimaryDomain = useSelector(getLoanOfficerPrimaryDomain);

  useLayoutEffect(() => {
    resetScrollPosition();
  }, [reduxActiveTab, showProfile]);

  // Mobile nav toggle state
  const [isNavOpen, setIsNavOpen] = useState(false);

  const hasBootstrapped = useRef(false);
  const uqualPanelOpenerRef = useRef<(() => void) | undefined>();

  // Prevent back/swipe navigation from leaving the dashboard.
  // Re-push the current URL whenever the user tries to go back, keeping them on the dashboard.
  useEffect(() => {
    globalThis.history.pushState(null, "", globalThis.location.href);

    const handlePopState = () => {
      globalThis.history.pushState(null, "", globalThis.location.href);
    };

    globalThis.addEventListener("popstate", handlePopState);
    return () => globalThis.removeEventListener("popstate", handlePopState);
  }, []);
  useEffect(() => {
    if (!hasBootstrapped.current && propsActiveTab && propsActiveTab !== reduxActiveTab) {
      dispatch(setProfileActiveTab(propsActiveTab));
    }
    hasBootstrapped.current = true;
  }, [dispatch, propsActiveTab, reduxActiveTab]);

  const prevShowProfile = useRef(false);
  useEffect(() => {
    if (showProfile && !prevShowProfile.current) {
      trackFSDashboardPageNavigated(getNavTrackingProps("Profile"));
    }
    prevShowProfile.current = showProfile;
  }, [showProfile]);

  const [showSpinner, setShowSpinner] = useState(false);
  const [spinnerDelay, setSpinnerDelay] = useState<NodeJS.Timeout | null>(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [pendingDocumentId, setPendingDocumentId] = useState<string | null>(null);
  const [isTaskCountSet, setIsTaskCountSet] = useState(false);

  const { notifications, removeNotification } = useNotificationContext();
  const userId = useSelector(getCurrentUserId);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const dealApplicationId = useSelector(getDealApplicationId);
  const isInterimDashboard = useSelector(getIsInterimDashboard);
  const [needsList, setNeedsList] = useState<NeedItem[]>([]);
  const sidebarTaskCount = useSelector(getTaskCount);
  const sessionHeldUnderReviewIds = useSelector(getSessionHeldUnderReviewIds);
  const [shouldFetchNeedsList, setShouldFetchNeedsList] = useState(false);
  const { data: dashboardData } = useGetDashboardItemsQuery(
    { dealApplicationId: nhDealApplicationId, userId },
    { skip: !nhDealApplicationId || !userId },
  );
  const [createNeeds, { isLoading: isCreatingNeeds, isSuccess: isCreateNeedsSucceeded }] = useCreateNeedsMutation();

  useEffect(() => {
    if (!dashboardData || isCreatingNeeds) return;

    const hasCreateNeedsTriggeredForSession = sessionStorage.getItem(DASHBOARD_CREATE_NEEDS_TRIGGERED) === DASHBOARD_CREATE_NEEDS_TRIGGERED_TRUE;
    const shouldEvaluateNeedsCreation =
      (dashboardData?.isAwaitingNeedsCreation === true || (isInterimDashboard && dashboardData?.isNew && dashboardData?.isCoborrowerNew)) &&
      !hasCreateNeedsTriggeredForSession;

    if (shouldEvaluateNeedsCreation) {
      // If application form is not required, skip needs creation and fetch needs directly
      if (!dashboardData?.isApplicationFormRequired) {
        setShouldFetchNeedsList(true);
      } else if (!isCreateNeedsSucceeded && !shouldFetchNeedsList) {
        // Application form is required — trigger needs creation
        void createNeeds({ dealApplicationId, userId, isInterimDashboard });
        sessionStorage.setItem(DASHBOARD_CREATE_NEEDS_TRIGGERED, DASHBOARD_CREATE_NEEDS_TRIGGERED_TRUE);
      }
    } else {
      setShouldFetchNeedsList(true);
    }
  }, [
    dashboardData?.isNew,
    dashboardData?.isCoborrowerNew,
    dashboardData?.isAwaitingNeedsCreation,
    dashboardData?.isApplicationFormRequired,
    isCreatingNeeds,
    isInterimDashboard,
    isCreateNeedsSucceeded,
    shouldFetchNeedsList,
    createNeeds,
    dealApplicationId,
    userId,
  ]);

  useEffect(() => {
    if (isCreateNeedsSucceeded && !shouldFetchNeedsList) {
      setShouldFetchNeedsList(true);
    }
  }, [isCreateNeedsSucceeded, shouldFetchNeedsList]);

  const needsQueryResult = useGetNeedsQuery(
    { nhDealApplicationId, userId },
    {
      skip: isCreatingNeeds || !shouldFetchNeedsList,
      refetchOnMountOrArgChange: false,
      selectFromResult: ({ data, isLoading, isFetching, error }) => ({
        data,
        isLoading,
        isFetching,
        error,
      }),
    },
  );

  const { data: needItems, isLoading: isNeedsListLoading, isFetching: isNeedsListFetching, error: needsListError } = needsQueryResult;

  const refetchNeedsList = needsQueryResult.refetch;

  // Hardcoded loan details loading flag (will be replaced with actual API call later)
  const isLoanDetailsLoading = false;
  const loanDetailsLoadError = false;

  const hideSpinner = () => {
    clearTimeout(spinnerDelay as NodeJS.Timeout);
    setShowSpinner(false);
  };

  const triggerSpinner = () => {
    const timeout = setTimeout(() => {
      setShowSpinner(true);
    }, 1000); // Delay of 1s before showing spinner
    setSpinnerDelay(timeout);
  };

  const getLoSalesDomain = (): string => {
    const domainName = loPrimaryDomain?.domainName?.toLowerCase();
    if (domainName === "retail") return "Retail";
    if (domainName === "cd") return "CD";
    return "Unassigned";
  };

  const getNavTrackingProps = (pageName: string) => ({
    email,
    prospectId,
    sessionId: sessionStorage.getItem(DASHBOARD_SESSION_ID),
    sessionStartTimestamp: sessionStorage.getItem(DASHBOARD_SESSION_START_TIMESTAMP),
    pageName,
    loanNumber: loanNumber || null,
    loanGuid: encompassLoanId || loanId || null,
    dealId: dealId || null,
    loSalesDomain: getLoSalesDomain(),
  });

  const PAGE_NAMES: Record<string, string> = {
    [NavigationIds.Home]: "Dashboard Home",
    [NavigationIds.Tasks]: "Tasks",
    [NavigationIds.Docs]: "Documents",
    [NavigationIds.LoanDetails]: "Loan Details",
  };

  const handleTabChange = (tab: string) => {
    if (tab !== NavigationIds.More) {
      triggerSpinner();
    }
    if (tab !== NavigationIds.More && PAGE_NAMES[tab]) {
      trackFSDashboardPageNavigated(getNavTrackingProps(PAGE_NAMES[tab]));
    }
    dispatch(setProfileActiveTab(tab));
  };

  const handleMobileNotificationClick = async (notificationId: string) => {
    await removeNotification(notificationId);
    setShowNotifications(false);
  };

  const handleMobileUqualNotificationClick = () => {
    uqualPanelOpenerRef.current?.();
  };

  const handleDocumentNotificationClick = (palInstanceId: string) => {
    if (palInstanceId) {
      setPendingDocumentId(palInstanceId);
      dispatch(setProfileActiveTab(NavigationIds.Docs));
    }
  };

  useEffect(() => {
    if (!isNeedsListLoading && !needsListError && needItems) {
      setNeedsList(needItems);

      const matchingNeeds = needItems.filter(
        (item) => getEffectiveTaskStatusForCount(item, sessionHeldUnderReviewIds) === TaskFilterType.ToDo,
      ).length;
      if (!isTaskCountSet || sidebarTaskCount !== matchingNeeds) {
        dispatch(setTaskCount(matchingNeeds));
        setIsTaskCountSet(true);
      }
    }
  }, [needItems, isNeedsListLoading, needsListError, sessionHeldUnderReviewIds]);

  const { data: featureFlag } = useGetFeatureFlagsQuery();

  const renderContent = () => {
    if (showProfile) {
      return <ProfileSettings />;
    }

    switch (reduxActiveTab) {
      case NavigationIds.Tasks:
        return (
          <Tasks
            onLoad={hideSpinner}
            isNeedsListLoading={isNeedsListLoading || isNeedsListFetching}
            isNeedsListFetching={isNeedsListFetching}
            isNeedsListError={Boolean(needsListError)}
            needsList={needsList}
            refetchNeedsList={refetchNeedsList}
          />
        );
      case NavigationIds.Docs:
        return <Documents onLoad={hideSpinner} pendingDocumentId={pendingDocumentId} onDocumentOpened={() => setPendingDocumentId(null)} />;
      case NavigationIds.LoanDetails:
        return (
          featureFlag?.loanDetailsEnabled === true && (
            <LoanDetails onLoad={hideSpinner} isLoanDetailsLoading={isLoanDetailsLoading} loanDetailsLoadError={loanDetailsLoadError} />
          )
        );
      case NavigationIds.More:
        return featureFlag?.loanDetailsEnabled === true && <LoanDetailsMenu onLoad={hideSpinner} />;
      case NavigationIds.Home:
      default:
        return <Home onLoad={hideSpinner} isCreatingNeeds={isCreatingNeeds || isNeedsListLoading} />;
    }
  };
  const hideFooter = reduxActiveTab === NavigationIds.More;
  const hideNotifications = reduxActiveTab === NavigationIds.More;

  const renderNavigation = () => (
    <SharedNavigation
      activeTab={reduxActiveTab}
      onTabChange={handleTabChange}
      isDesktop={isDesktop}
      isTablet={isTablet}
      isNavOpen={isNavOpen}
      setIsNavOpen={setIsNavOpen}
    />
  );

  return (
    <GlobalOuterLayout>
      <div className="shared-spinner">{showSpinner && <Spinner isApi />}</div>
      {isDesktop && (
        <>
          {renderNavigation()}
          <div className="global-outer-layout__wrapper">
            <GlobalHeader onDocumentNotificationClick={handleDocumentNotificationClick} hideNotifications={hideNotifications} />
            <main className="main-content">
              {renderContent()}
              <Footer />
            </main>
          </div>
        </>
      )}
      {!isDesktop && (
        <div className="mobile-container">
          {!showProfile && (
            <GlobalHeader
              onNotificationsClick={() => setShowNotifications(!showNotifications)}
              onDocumentNotificationClick={handleDocumentNotificationClick}
              hideNotifications={hideNotifications}
              isNavOpen={isNavOpen}
              setIsNavOpen={setIsNavOpen}
              uqualPanelOpenerRef={uqualPanelOpenerRef}
            />
          )}
          <div className={`mobile-content ${isBannerVisible ? "mobile-content--banner-visible" : ""}`}>
            {showNotifications ? (
              <PalNotificationHandler
                notifications={notifications}
                onNotificationClick={handleMobileNotificationClick}
                onDocumentNotificationClick={handleDocumentNotificationClick}
                onUqualNotificationClick={handleMobileUqualNotificationClick}
                isOpen={showNotifications}
                onClose={() => setShowNotifications(false)}
                triggerElementSelector=".global-header__notification-button"
              />
            ) : (
              <>
                {renderContent()}
                {!hideFooter && <Footer />}
              </>
            )}
          </div>
          {renderNavigation()}
        </div>
      )}
    </GlobalOuterLayout>
  );
};

export const SharedLayout: React.FC<SharedLayoutProps> = (props: SharedLayoutProps) => {
  return (
    <NotificationProvider>
      <SharedLayoutContent {...props} />
    </NotificationProvider>
  );
};

export default SharedLayout;
